import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PublicUser, ThemePreference } from '@us/shared';

/** 사용자 액센트 색을 전역 CSS 변수로 주입(어드민 수정 시 즉시 반영). */
export function applyAccentColors(users: PublicUser[]): void {
  const a = users.find((u) => u.id === 'userA')?.accentColor;
  const b = users.find((u) => u.id === 'userB')?.accentColor;
  const root = document.documentElement;
  if (a) root.style.setProperty('--accent-a', a);
  if (b) root.style.setProperty('--accent-b', b);
}

const STORAGE_KEY = 'us-theme';

interface ThemeCtx {
  theme: ThemePreference;
  isDark: boolean;
  setTheme: (t: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeCtx | null>(null);

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveDark(theme: ThemePreference): boolean {
  return theme === 'dark' || (theme === 'system' && systemPrefersDark());
}

export function ThemeProvider({
  defaultTheme = 'system',
  children,
}: {
  defaultTheme?: ThemePreference;
  children: ReactNode;
}) {
  const [theme, setThemeState] = useState<ThemePreference>(
    () => (localStorage.getItem(STORAGE_KEY) as ThemePreference) || defaultTheme,
  );
  const [isDark, setIsDark] = useState(() => resolveDark(theme));

  const setTheme = useCallback((t: ThemePreference) => {
    setThemeState(t);
    localStorage.setItem(STORAGE_KEY, t);
  }, []);

  useEffect(() => {
    const update = () => {
      const dark = resolveDark(theme);
      setIsDark(dark);
      document.documentElement.classList.toggle('dark', dark);
    };
    update();
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
  }, [theme]);

  const value = useMemo(() => ({ theme, isDark, setTheme }), [theme, isDark, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
